import { TenantConfig } from '@/services/tenant.service'

const TENANT_CONFIG_CACHE_KEY = 'tenant_config_cache'
const TENANT_CONFIG_CACHE_TTL = 30 * 60 * 1000 // 30 minutes

interface CachedTenantConfig {
  config: TenantConfig
  timestamp: number
  tenantId: string
}

/**
 * Get tenant config from cookie cache (server-side)
 */
export function getTenantConfigFromCookie(req: {
  cookies?: Record<string, string> | { [key: string]: string }
}): TenantConfig | null {
  const cookies = req.cookies as Record<string, string> | undefined
  if (!cookies?.[TENANT_CONFIG_CACHE_KEY]) {
    return null
  }

  try {
    const cached: CachedTenantConfig = JSON.parse(cookies[TENANT_CONFIG_CACHE_KEY])

    // Check if cache is still valid
    if (Date.now() - cached.timestamp < TENANT_CONFIG_CACHE_TTL) {
      return cached.config
    }
  } catch {
    // Invalid cache, ignore
  }

  return null
}

/**
 * Set tenant config in cookie (server-side)
 */
export function setTenantConfigCookie(
  res: { setHeader?: (name: string, value: string) => void },
  tenantId: string,
  config: TenantConfig
): void {
  if (!res.setHeader) {
    return
  }

  const cached: CachedTenantConfig = {
    config,
    timestamp: Date.now(),
    tenantId,
  }

  const cookieValue = JSON.stringify(cached)
  res.setHeader(
    'Set-Cookie',
    `${TENANT_CONFIG_CACHE_KEY}=${encodeURIComponent(cookieValue)}; Path=/; Max-Age=${TENANT_CONFIG_CACHE_TTL / 1000}; SameSite=Lax; HttpOnly`
  )
}

/**
 * Get tenant config from localStorage (client-side)
 */
export function getTenantConfigFromStorage(tenantId: string): TenantConfig | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const cachedStr = localStorage.getItem(`${TENANT_CONFIG_CACHE_KEY}_${tenantId}`)
    if (!cachedStr) {
      return null
    }

    const cached: CachedTenantConfig = JSON.parse(cachedStr)

    // Check if cache is still valid and matches current tenant
    if (cached.tenantId === tenantId && Date.now() - cached.timestamp < TENANT_CONFIG_CACHE_TTL) {
      return cached.config
    }

    // Cache expired or wrong tenant, remove it
    localStorage.removeItem(`${TENANT_CONFIG_CACHE_KEY}_${tenantId}`)
  } catch {
    // Invalid cache, ignore
  }

  return null
}

/**
 * Set tenant config in localStorage (client-side)
 */
export function setTenantConfigStorage(tenantId: string, config: TenantConfig): void {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const cached: CachedTenantConfig = {
      config,
      timestamp: Date.now(),
      tenantId,
    }
    localStorage.setItem(`${TENANT_CONFIG_CACHE_KEY}_${tenantId}`, JSON.stringify(cached))
  } catch {
    // localStorage might be disabled, ignore
  }
}

/**
 * Remove tenant config from localStorage (client-side)
 */
export function clearTenantConfigStorage(tenantId: string): void {
  if (typeof window === 'undefined') {
    return
  }

  try {
    localStorage.removeItem(`${TENANT_CONFIG_CACHE_KEY}_${tenantId}`)
  } catch {
    // localStorage might be disabled, ignore
  }
}
